import React, { useState, useEffect, useRef, ReactElement, ReactNode } from 'react';
import cx from 'classnames';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@ui/lib/utils';

/**
 * Open/close is timed to how far the panel actually travels, because the same accordion holds a single day's
 * reports in one place and a whole sprint of days in another. One fixed duration can only suit one of them:
 * it either rushes the tall panel through a screen's worth of movement or drags out the short one. So a base
 * duration covers the short panels and taller content earns more time, up to a ceiling — past a certain
 * distance a longer animation is just waiting.
 */
const BASE_MS = 260;
const MS_PER_PX = 0.25;
const MAX_MS = 700;
/**
 * Closing is not the opening played backwards, and it wants its own curve. Opening decelerates into place
 * (`ease-out`), which is right for content arriving. Closing takes the symmetric curve (`ease-in-out`) and a
 * little less time. The two curves it isn't are both worse: `ease-out` spends the distance in the first few
 * frames and then creeps through the last handful of pixels, so the panel looks gone while the page is still
 * settling; `ease-in` does the reverse and slams shut, which reads as forced.
 */
const CLOSE_RATIO = 0.75;
const MIN_CLOSE_MS = 200;
const durationFor = (distance: number, open: boolean): number => {
  const ms = Math.min(MAX_MS, BASE_MS + distance * MS_PER_PX);
  return Math.round(open ? ms : Math.max(MIN_CLOSE_MS, ms * CLOSE_RATIO));
};

interface IAccordion extends React.HTMLAttributes<HTMLDivElement> {
  titleContent: string | JSX.Element;
  /** Rendered beside the expand toggle (not inside the `<button>`) so icon buttons are not nested interactives. */
  headerActions?: ReactNode;
  children: ReactNode;
  isOpen?: boolean;
  /** Fired when the user opens or closes it, so header actions can react to being open (or not). */
  onOpenChange?: (open: boolean) => void;
  containerClassName?: string;
  contentClassName?: string;
  className?: string;
  openContainerClassName?: string;
}

const Accordion = ({
  titleContent,
  headerActions,
  children,
  isOpen,
  onOpenChange,
  containerClassName,
  contentClassName,
  className,
  openContainerClassName,
  id,
}: IAccordion): ReactElement => {
  const [isActive, setIsActive] = useState(isOpen);
  const contentRef = useRef<HTMLDivElement>(null);
  const [duration, setDuration] = useState(BASE_MS);

  // The content keeps its natural height even while the grid row is collapsed to 0fr (it's clipped, not
  // squashed), so this measures the distance the panel is about to cover either way. Set alongside the state
  // it belongs to, so both land in the same commit and the transition starts with the right duration.
  const openWith = (next: boolean | undefined): void => {
    setDuration(durationFor(contentRef.current?.scrollHeight ?? 0, Boolean(next)));
    setIsActive(next);
  };

  // Sync with the controlling prop (e.g. an "expand all" toggle) so the height change
  // animates in place instead of the parent forcing a remount.
  useEffect(() => openWith(isOpen), [isOpen]);

  /**
   * A closed panel keeps its content, clipped to a zero-height row — which is what lets it animate open. The
   * catch is that the clipped content still counts towards the intrinsic height of the flex column it sits
   * in, so a page of closed accordions scrolls far past where it appears to end. `contain: size` settles
   * that: the box is sized without asking its contents.
   *
   * It can only go on once the panel has finished closing. While the row is still animating, the container's
   * height is measured from that content, so containing it mid-flight collapses the panel in one frame
   * instead of letting it travel.
   */
  const [settled, setSettled] = useState(!isOpen);
  useEffect(() => {
    if (isActive) {
      setSettled(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setSettled(true), duration);
    return () => window.clearTimeout(timer);
  }, [isActive, duration]);

  return (
    // Merged rather than concatenated: `bg-background` is only a default, so a caller passing its own
    // background (a card, a weekend day) has to be able to drop it instead of racing it in the stylesheet.
    <div id={id} className={cn('bg-background', containerClassName, isActive && openContainerClassName)}>
      <div className="flex w-full items-center">
        <button
          type="button"
          className={cx('min-w-0 flex-1 cursor-pointer border-none bg-transparent px-6 py-5 text-left', className)}
          onClick={() => {
            const next = !isActive;
            openWith(next);
            onOpenChange?.(next);
          }}
          aria-expanded={Boolean(isActive)}
        >
          <div className="flex flex-nowrap items-center">
            {isActive ? (
              <ChevronUp className="mr-2 size-5 shrink-0" aria-hidden />
            ) : (
              <ChevronDown className="mr-2 size-5 shrink-0" aria-hidden />
            )}
            <div className="w-full min-w-0">{titleContent}</div>
          </div>
        </button>
        {headerActions != null && <div className="flex shrink-0 items-center gap-2 pr-6">{headerActions}</div>}
      </div>
      <div
        className={cx(
          'grid transition-[grid-template-rows]',
          isActive ? 'grid-rows-[1fr] ease-out' : 'grid-rows-[0fr] ease-in-out',
        )}
        style={{ transitionDuration: `${duration}ms` }}
      >
        <div className={cx('overflow-hidden', !isActive && settled && '[contain:size_layout]')}>
          <div ref={contentRef} className={cx(contentClassName)}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Accordion;
